import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Select, Tabs, Input } from '@artosera/ui';
import {
  clesPresentation,
  useActionSelection,
  useCompteApprobations,
} from '../../api/presentation.js';
import { useQuery } from '@tanstack/react-query';
import { appelDeRequete } from '../../api/cles.js';
import { listerSelectionsPresentation } from './horsLigne/liste.js';
import { useContacts } from '../../api/contacts.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NouvelleSelection } from './NouvelleSelection.js';
import { DemandesApprobation } from './DemandesApprobation.js';
import { ErreurPresentation } from './ErreurPresentation.js';
import { DisponibilitePresentation } from './horsLigne/PreparationPresentation.js';
import styles from './Presentation.module.css';

export function PresentationPage() {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const peutLire =
    galerieAModule(galerie, 'rendez-vous-client') && galerieALaction(galerie, 'presentation:read');
  const peutEcrire = navigator.onLine && peutLire && galerieALaction(galerie, 'presentation:write');
  const peutApprouver = peutLire && galerieALaction(galerie, 'negociation:approuver');
  const [archives, setArchives] = useState('actives');
  const [contactId, setContactId] = useState('tous');
  const [rechercheContact, setRechercheContact] = useState('');
  const [page, setPage] = useState(1);
  const contacts = useContacts(
    galerieSlug,
    { q: rechercheContact, parPage: 200 },
    { enabled: peutLire && galerieALaction(galerie, 'contacts:read') },
  );
  const filtres = {
    ...(archives === 'toutes' ? {} : { archivee: archives === 'archivees' }),
    ...(contactId === 'tous' ? {} : { contactId }),
    page,
  };
  const liste = useQuery({
    queryKey: clesPresentation.liste(galerieSlug, filtres),
    queryFn: (contexte) =>
      listerSelectionsPresentation(
        { compteId: compte.id, galerieSlug },
        filtres,
        appelDeRequete(contexte),
      ),
    enabled: peutLire,
    networkMode: 'always',
  });
  const action = useActionSelection(galerieSlug);
  const compteur = useCompteApprobations(galerieSlug, peutApprouver);
  if (!peutLire) return <p role="alert">{t('interdit')}</p>;
  return (
    <div className={styles.page}>
      <header className={styles.actions}>
        <h1>{t('titre')}</h1>
        {peutEcrire ? <NouvelleSelection key={`${compte.id}:${galerieSlug}`} /> : null}
      </header>
      {compteur.data && compteur.data.compte > 0 ? (
        <p role="status" className={styles.bandeau}>
          {t('approbations.enAttente', { count: compteur.data.compte })}
        </p>
      ) : null}
      <ErreurPresentation erreur={compteur.error} recharger={() => void compteur.refetch()} />
      <Tabs
        tabs={[
          {
            value: 'selections',
            label: t('selections'),
            content: (
              <div className={styles.page}>
                <div className={styles.filtres}>
                  <label className={styles.champ}>
                    {t('archives')}
                    <Select
                      value={archives}
                      onValueChange={(valeur) => {
                        setArchives(valeur);
                        setPage(1);
                      }}
                      options={['actives', 'archivees', 'toutes'].map((value) => ({
                        value,
                        label: t(value),
                      }))}
                    />
                  </label>
                  {galerieALaction(galerie, 'contacts:read') ? (
                    <>
                      <label className={styles.champ}>
                        {t('rechercheContact')}
                        <Input
                          value={rechercheContact}
                          onChange={(event) => setRechercheContact(event.target.value)}
                        />
                      </label>
                      <Select
                        aria-label={t('tousContacts')}
                        value={contactId}
                        onValueChange={(valeur) => {
                          setContactId(valeur);
                          setPage(1);
                        }}
                        options={[
                          { value: 'tous', label: t('tousContacts') },
                          ...(contacts.data?.contacts ?? []).map((contact) => ({
                            value: contact.id,
                            label: contact.nomAffiche,
                          })),
                        ]}
                      />
                    </>
                  ) : null}
                </div>
                {liste.isPending ? <p role="status">{t('chargement')}</p> : null}
                <ErreurPresentation
                  erreur={liste.error ?? action.error ?? contacts.error}
                  recharger={() => {
                    action.reset();
                    void liste.refetch();
                    if (galerieALaction(galerie, 'contacts:read')) void contacts.refetch();
                  }}
                />
                {liste.data?.selections.length === 0 ? <p>{t('vide')}</p> : null}
                <ul className={styles.liste}>
                  {liste.data?.selections.map((selection) => (
                    <li className={styles.carte} key={selection.id}>
                      <div className={styles.actions}>
                        <Link
                          className={styles.lien}
                          to={`/${galerieSlug}/presentation/${selection.id}`}
                        >
                          {selection.titre}
                        </Link>
                        {peutEcrire ? (
                          <>
                            <Button
                              variant="secondary"
                              disabled={action.isPending}
                              onClick={() => action.mutate({ type: 'dupliquer', id: selection.id })}
                            >
                              {t('dupliquer')}
                            </Button>
                            <Button
                              variant="secondary"
                              disabled={action.isPending}
                              onClick={() =>
                                action.mutate({
                                  type: 'archiver',
                                  id: selection.id,
                                  archivee: !selection.archivee,
                                })
                              }
                            >
                              {t(selection.archivee ? 'desarchiver' : 'archiver')}
                            </Button>
                          </>
                        ) : null}
                      </div>
                      {selection.note ? <p>{selection.note}</p> : null}
                      <DisponibilitePresentation selectionId={selection.id} />
                    </li>
                  ))}
                </ul>
                {liste.data && liste.data.pagination.pages > 1 ? (
                  <div className={styles.actions}>
                    <Button
                      variant="secondary"
                      disabled={page <= 1 || liste.isFetching}
                      onClick={() => setPage(page - 1)}
                    >
                      {t('precedent')}
                    </Button>
                    <span>{t('page', { page, pages: liste.data.pagination.pages })}</span>
                    <Button
                      variant="secondary"
                      disabled={page >= liste.data.pagination.pages || liste.isFetching}
                      onClick={() => setPage(page + 1)}
                    >
                      {t('suivant')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ),
          },
          { value: 'demandes', label: t('demandes'), content: <DemandesApprobation /> },
        ]}
      />
    </div>
  );
}
